import { useEffect, useRef, useState } from "react";

import BottomSubmit from "../../components/BottomSubmit";
import ConfigLoader from "../../components/ConfigLoader";
import ConfigHeaderActions from "../../components/ConfigHeaderActions";
import useConfigHeader from "../../hooks/useConfigHeader";

import './styles.css'

import api from '@helpers/api'

import CommunityAssetImageField from "@features/uploadAsset/components/CommunityAssetImageField";
import { AssetCategory } from "@features/uploadAsset/constants/assetCategories";

import WelcomeVideo from "./components/WelcomeVideo";

import { Button, ButtonGroup, Input, Popper, Text } from "@fragments";

import { IconBrandInstagram, IconBrandTiktok, IconBrandWhatsapp, IconChevronDown, IconGeometry, IconBrandYoutube, IconCheck, IconDots, IconHelpCircle, IconLink, IconMail, IconMessageCircle, IconPencil, IconPhoto, IconPlus, IconTrash, IconWallpaper, IconWorld, IconLock } from "@tabler/icons-react";


import { useCommunity } from "contexts/community";

import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Brand names (`label`) are never translated; the remaining labels carry a key
// resolved at render so they follow the active language.
const LINK_TYPES = [
    { value: 'website', labelKey: 'general.links.types.website', icon: <IconWorld size={18} /> },
    { value: 'support', labelKey: 'general.links.types.support', icon: <IconHelpCircle size={18} /> },
    { value: 'whatsapp', label: 'WhatsApp', icon: <IconBrandWhatsapp size={18} /> },
    { value: 'instagram', label: 'Instagram', icon: <IconBrandInstagram size={18} /> },
    { value: 'tiktok', label: 'TikTok', icon: <IconBrandTiktok size={18} /> },
    { value: 'youtube', label: 'YouTube', icon: <IconBrandYoutube size={18} /> },
    { value: 'email', labelKey: 'general.links.types.email', icon: <IconMail size={18} /> },
    { value: 'default', labelKey: 'general.links.types.default', icon: <IconLink size={18} /> },
];

function linkTypeLabel(t, value) {
    const entry = LINK_TYPES.find((linkType) => linkType.value === value);
    if (!entry) return '';
    return entry.label ?? t(entry.labelKey);
}

const getLinkTypes = (t) => LINK_TYPES.map((linkType) => ({
    value: linkType.value,
    label: linkTypeLabel(t, linkType.value),
    icon: linkType.icon,
}));

const LINK_TYPE_ICON = LINK_TYPES.reduce((acc, linkType) => {
    acc[linkType.value] = linkType.icon;
    return acc;
}, {});

const FREE_TITLE_TYPES = new Set(['website', 'default']);

const VALID_LINK_TYPES = new Set(LINK_TYPES.map((linkType) => linkType.value));

const SUPPORT_TYPE = 'support';
const SUPPORT_MODES = { CHAT: 'chat', URL: 'url' };

function defaultTitleForType(t, type) {
    return FREE_TITLE_TYPES.has(type) ? '' : linkTypeLabel(t, type);
}

/**
 * A support link resolves either to an external URL or to the platform chat
 * with the owner. Older payloads carry no `mode`: a URL means external, none
 * means chat. In chat mode the API's `url` is computed (never stored), so it
 * is not something to edit here.
 */
function normalizeSupportLink(link) {
    if (link?.type !== SUPPORT_TYPE) return link;
    const mode = link.mode ?? (link.url ? SUPPORT_MODES.URL : SUPPORT_MODES.CHAT);
    return { ...link, mode, url: mode === SUPPORT_MODES.CHAT ? '' : link.url };
}

// Every community has a support link (chat by default). A payload without one
// (older backend) gets it locally so the row is always there to configure.
function ensureSupportLink(links, t) {
    if (links.some((link) => link?.type === SUPPORT_TYPE)) return links;
    return [
        ...links,
        { title: defaultTitleForType(t, SUPPORT_TYPE), url: '', type: SUPPORT_TYPE, mode: SUPPORT_MODES.CHAT },
    ];
}

/**
 * Older backend responses returned community links with `title` and `type`
 * swapped (legacy bug in CommunityResource). Detect that shape and put them
 * back in the right slots so the next save persists the correct payload.
 */
function healLink(link) {
    if (!link || typeof link !== 'object') return link;
    const typeIsValid = VALID_LINK_TYPES.has(link.type);
    const titleLooksLikeType = VALID_LINK_TYPES.has(link.title);
    if (!typeIsValid && titleLooksLikeType) {
        return { ...link, title: link.type, type: link.title };
    }
    return link;
}

/**
 * Best-effort detection of the link type from a URL.
 * Returns one of the LINK_TYPES values, or null if we can't tell.
 */
function detectLinkType(rawUrl) {
    if (!rawUrl || typeof rawUrl !== 'string') return null;
    const value = rawUrl.trim().toLowerCase();
    if (!value) return null;

    if (value.startsWith('mailto:') || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'email';
    }

    // Normalize to a hostname when possible
    let host = '';
    try {
        const candidate = value.includes('://') ? value : 'https://' + value;
        host = new URL(candidate).hostname.replace(/^www\./, '');
    } catch (_) {
        host = value;
    }

    if (/(^|\.)wa\.me$|whatsapp\.com$|whatsapp\.net$/.test(host)) return 'whatsapp';
    if (/(^|\.)instagram\.com$|(^|\.)instagr\.am$/.test(host)) return 'instagram';
    if (/(^|\.)tiktok\.com$/.test(host)) return 'tiktok';
    if (/(^|\.)youtube\.com$|(^|\.)youtu\.be$/.test(host)) return 'youtube';

    if (/(^|\.)(help|support|atendimento|suporte)\./.test(host) || /\/(help|support|suporte|atendimento)(\/|$)/.test(value)) {
        return 'support';
    }

    if (value.startsWith('http://') || value.startsWith('https://')) return 'website';

    return null;
}



export default function General({ info, handleRefresh }) {
    const { t } = useTranslation('settings');

    // Page identity lives in the floating settings header (the sidebar entry
    // keeps the icon); the status + actions buttons are portaled into it.
    useConfigHeader({ title: t('general.title') });

    const [hasUpdates, setHasUpdates] = useState(false)
    const [loading, setLoading] = useState(true)

    let { slug } = useParams();

    const { loading: communityLoading, community, loadCommunities } = useCommunity();

    const REF_config = useRef()
    const REF_popper = useRef()
    const REF_statusPopper = useRef()
    const REF_welcomeVideo = useRef()

    const communityId = community?.getBySlug?.(slug)?.id;
    const initialStatus = info?.status ?? community?.getBySlug?.(slug)?.status ?? 'draft';
    const [status, setStatus] = useState(initialStatus);


    useEffect(() => {
        if (!communityLoading) handleLoad(true)
    }, [communityLoading])

    async function handleLoad() {
        if (info) {
            REF_config.current = {
                ...info,
                links: ensureSupportLink(
                    (Array.isArray(info.links) ? info.links : []).map(healLink).map(normalizeSupportLink),
                    t,
                ),
            }
            setLoading(false)
            setStatus(info.status ?? initialStatus);
        }
    }

    async function handleSubmit() {
        try {
            const payload = getPayload();
            if (!payload) return;

            const { data } = await api.patch(`/community/${communityId}`, payload)

            if (data?.id) {
                // The welcome card's title/text go through their own endpoint;
                // on failure the field already reported it and stays dirty.
                const welcomeSaved = (await REF_welcomeVideo.current?.save?.()) !== false
                if (welcomeSaved) {
                    window.setSnackbar(t('general.feedback.saved'))
                    setHasUpdates(false)
                }
                loadCommunities?.()
                handleRefresh?.()
            } else {
                window.setSnackbar(t('general.feedback.saveError'))
            }
        } catch (e) {
            console.log(e)
            window.setSnackbar(t('general.feedback.saveError'))
        }

        function getPayload() {
            const config = REF_config.current;
            const payload = {};

            // Name (required, 3-25 chars)
            if (!config.name || config.name.trim().length === 0) {
                window.setSnackbar(t('general.validation.nameRequired'));
                return null;
            }

            const nameLength = config.name.trim().length;
            if (nameLength < 3 || nameLength > 25) {
                window.setSnackbar(t('general.validation.nameLength'));
                return null;
            }

            payload.name = config.name.trim();

            // Description (optional, nullable)
            const description = (config.description ?? '').trim();
            payload.description = description.length > 0 ? description : null;

            // Links (optional, max 10)
            if (config.links && config.links.length > 0) {
                if (config.links.length > 10) {
                    window.setSnackbar(t('general.validation.maxLinks'));
                    return null;
                }
                payload.links = config.links.map((l) => {
                    if (l.type === SUPPORT_TYPE) {
                        const wantsUrl = (l.mode ?? (l.url ? SUPPORT_MODES.URL : SUPPORT_MODES.CHAT)) === SUPPORT_MODES.URL;
                        const mode = wantsUrl && (l.url ?? '').trim() ? SUPPORT_MODES.URL : SUPPORT_MODES.CHAT;
                        // Chat mode sends no URL: core computes the owner's chat link on read.
                        return mode === SUPPORT_MODES.URL
                            ? { title: l.title, url: l.url, type: SUPPORT_TYPE, mode }
                            : { title: l.title, type: SUPPORT_TYPE, mode };
                    }
                    return { title: l.title, url: l.url, type: l.type ?? 'website' };
                });
            }

            // Banner & hero: URLs already uploaded via the presign flow.
            if (config.banner !== undefined) payload.banner = config.banner || null;
            if (config.hero !== undefined) payload.hero = config.hero || null;
            if (config.hero_condensed !== undefined) payload.hero_condensed = config.hero_condensed || null;

            return payload;
        }
    }

    function handleChange(key, value) {
        REF_config.current[key] = value
        setHasUpdates(true)
    }

    async function handleChangeStatus(next) {
        if (next === status) return;
        try {
            const { data } = await api.patch(`/community/${communityId}/status`, { status: next });
            if (data?.id) {
                setStatus(next);
                window.setSnackbar(next === 'published' ? t('general.status.publishedFeedback') : t('general.status.unpublishedFeedback'));
                loadCommunities?.();
                handleRefresh?.();
            } else {
                window.setSnackbar(t('general.status.changeError'));
            }
        } catch (e) {
            console.log(e);
            window.setSnackbar(t('general.status.changeError'));
        }
    }

    function statusOptionLabel(title, description) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{title}</span>
                <span style={{ fontSize: 12, opacity: 0.6, lineHeight: 1.3 }}>{description}</span>
            </div>
        );
    }

    function handleOpenStatus(event) {
        const anchor = event.currentTarget;
        REF_statusPopper.current?.open(
            [
                {
                    id: 'published',
                    label: statusOptionLabel(
                        t('common:status.published'),
                        t('general.status.publishedDescription'),
                    ),
                    variant: status === 'published' ? 'info' : undefined,
                    icon: <IconWorld size={20} />,
                    onClick: async () => {
                        await handleChangeStatus('published');
                    },
                },
                {
                    id: 'draft',
                    label: statusOptionLabel(
                        t('common:status.draft'),
                        t('general.status.draftDescription'),
                    ),
                    variant: status === 'draft' ? 'info' : undefined,
                    icon: <IconGeometry size={20} />,
                    onClick: async () => {
                        await handleChangeStatus('draft');
                    },
                },
            ],
            'bottom-end',
            anchor,
        );
    }

    function handleDelete() {
        window.openConfirm?.({
            title: t('general.delete.title'),
            headline: t('general.delete.headline'),
            subtitle: t('general.delete.subtitle'),
            buttons: [
                {
                    text: t('common:actions.delete'),
                    variant: 'contained',
                    color: 'error',
                    onClick: async () => {
                        try {
                            await api.delete(`/community/${communityId}`);
                            window.setSnackbar(t('general.delete.success'));
                            loadCommunities?.(() => window.navigate?.('/'));
                        } catch (e) {
                            console.log(e);
                            window.setSnackbar(t('general.delete.error'));
                        }
                    },
                },
                {
                    text: t('common:actions.cancel'),
                    variant: 'text',
                    color: 'overlay',
                },
            ],
        });
    }

    function handleOpenActions(event) {
        const anchor = event.currentTarget;
        REF_popper.current?.open(
            [
                {
                    id: 'delete',
                    label: t('general.actions.deleteCommunity'),
                    icon: <IconTrash size={18} />,
                    variant: 'danger',
                    onClick: async () => {
                        handleDelete();
                    },
                },
            ],
            'bottom',
            anchor,
        );
    }

    function Hero() {
        return (
            <>
                <Text.h3
                    value={t('general.hero.title')}
                    left={<IconWallpaper />}
                />
                <Text.h4
                    contrast={5}
                    value={t('general.hero.subtitle')}
                />
                <CommunityAssetImageField
                    className="mt-2"
                    variant="dark"
                    aspectRatio={16 / 3}
                    fullWidth
                    imageMaxWidth={1700}
                    communityId={communityId}
                    category={AssetCategory.COMMUNITY_HERO}
                    value={REF_config.current.hero}
                    onChange={(url) => handleChange('hero', url)}
                    title={t('general.hero.fieldTitle')}
                />

                <Text.h4
                    contrast={5}
                    className="mt-3"
                    value={t('general.hero.condensedSubtitle')}
                />
                <CommunityAssetImageField
                    className="mt-2 upload-banner"
                    variant="dark"
                    aspectRatio={6 / 4}
                    imageMaxWidth={1200}
                    communityId={communityId}
                    category={AssetCategory.COMMUNITY_HERO_CONDENSED}
                    value={REF_config.current.hero_condensed}
                    onChange={(url) => handleChange('hero_condensed', url)}
                    title={t('general.hero.condensedFieldTitle')}
                />
            </>
        )
    }

    function Profile() {
        return (
            <>
                <Text.h3
                    value={t('general.profile.title')}
                    left={<IconPhoto />}
                    className="mt-4 mb-2"
                />
                <Text.h5
                    contrast={5}
                    value={t('general.profile.subtitle')}
                />
                <CommunityAssetImageField
                    className="mt-2 upload-banner"
                    variant="dark"
                    size={135}
                    communityId={communityId}
                    category={AssetCategory.COMMUNITY_BANNER}
                    value={REF_config.current.banner}
                    onChange={(url) => handleChange('banner', url)}
                    title={t('general.profile.fieldTitle')}
                />
            </>
        )
    }

    function Links() {
        const [links, setLinks] = useState(REF_config.current.links)

        const LinkInfo = ({ data, index }) => {
            const initialType = data.type || 'website';
            const initialTitle = FREE_TITLE_TYPES.has(initialType)
                ? (data.title || '')
                : (data.title || defaultTitleForType(t, initialType));

            const [open, setOpen] = useState(!!data.open);
            const [title, setTitle] = useState(initialTitle);
            const [link, setLink] = useState(data.url);
            const [type, setType] = useState(initialType);
            const [mode, setMode] = useState(data.mode ?? (data.url ? SUPPORT_MODES.URL : SUPPORT_MODES.CHAT));
            const isSupport = type === SUPPORT_TYPE;
            // The first support row is the community's support contact: it always
            // exists and cannot be removed or retyped.
            const isPrimarySupport = isSupport && index === links.findIndex((entry) => entry?.type === SUPPORT_TYPE);
            // Track whether the user manually picked a type; if so, stop
            // auto-detecting from the URL on subsequent changes.
            const [typeAutoFilled, setTypeAutoFilled] = useState(!data.type || data.type === 'website');

            // Keep REF_config in sync with the (possibly auto-filled) initial title.
            useEffect(() => {
                if (REF_config.current.links?.[index]) {
                    REF_config.current.links[index].title = initialTitle;
                    REF_config.current.links[index].type = initialType;
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, []);

            useEffect(() => {
                REF_config.current.links[index].open = open;
            }, [open]);

            // Switch the effective title whenever the type changes between
            // free-title and forced-default-title categories.
            function applyTypeChange(nextType, linkCopy) {
                linkCopy[index].type = nextType;
                if (!FREE_TITLE_TYPES.has(nextType)) {
                    const forced = defaultTitleForType(t, nextType);
                    linkCopy[index].title = forced;
                    setTitle(forced);
                }
                setType(nextType);
            }

            function applyModeChange(nextMode) {
                const linkCopy = [...links];
                linkCopy[index].mode = nextMode;
                REF_config.current.links = [...linkCopy];
                setMode(nextMode);
                setHasUpdates(true);
            }

            if (!open)
                return (
                    <div className="description-link-row">
                        <div className="description-link bsf-9">
                            <span className="url-icon" style={{ display: 'inline-flex' }}>
                                {LINK_TYPE_ICON[type] || <IconLink size={18} />}
                            </span>
                            <span className="title">{title || <i>{t('general.links.unnamed')}</i>}</span>
                            <span className="url">
                                {isSupport && mode === SUPPORT_MODES.CHAT ? t('general.links.support.chatSummary') : link}
                            </span>
                            <span className="type" style={{ opacity: 0.6, marginLeft: 8 }}>
                                {linkTypeLabel(t, type) || type}
                            </span>
                            <Button
                                color="overlay"
                                onClick={() => setOpen(true)}
                                size="small"
                                icon={<IconPencil />}
                            />
                        </div>
                    </div>
                );

            return (
                <div className="link-info-block mt-2 mb-3 px-2 py-2">
                    {isPrimarySupport ? (
                        <div className="support-link-head">
                            <Text.label left={<IconHelpCircle />} value={t('general.links.types.support')} />
                            <Text.caption value={t('general.links.support.alwaysPresent')} />
                        </div>
                    ) : (
                        <Input.Select
                            label={t('general.links.typeLabel')}
                            value={type}
                            // One support contact per community: the primary row owns that type.
                            options={getLinkTypes(t).filter((option) => option.value !== SUPPORT_TYPE || isSupport)}
                            onInput={(value) => {
                                const linkCopy = [...links];
                                applyTypeChange(value, linkCopy);
                                REF_config.current.links = [...linkCopy];
                                // Manual pick — disable further auto-detection
                                // on this link from URL edits.
                                setTypeAutoFilled(false);
                            }}
                        />
                    )}

                    {isSupport && (
                        <div className="support-mode mt-2">
                            <Text.label value={t('general.links.support.modeLabel')} />
                            <ButtonGroup
                                className="mt-1"
                                grow
                                size="small"
                                value={mode}
                                onChange={applyModeChange}
                                options={[
                                    { value: SUPPORT_MODES.CHAT, label: t('general.links.support.modeChat'), icon: <IconMessageCircle /> },
                                    { value: SUPPORT_MODES.URL, label: t('general.links.support.modeUrl'), icon: <IconLink /> },
                                ]}
                            />
                            <Text.p
                                className="mt-1"
                                contrast={6}
                                value={mode === SUPPORT_MODES.CHAT
                                    ? t('general.links.support.chatHint')
                                    : t('general.links.support.urlHint')}
                            />
                        </div>
                    )}

                    {FREE_TITLE_TYPES.has(type) && (
                        <Input.Text
                            className="mt-2"
                            emoji
                            variant="dark"
                            size={25}
                            value={title}
                            onInput={(value) => {
                                const linkCopy = [...links];
                                linkCopy[index].title = value;
                                REF_config.current.links = [...linkCopy];
                                setTitle(value);
                            }}
                            label={t('general.links.titleLabel')}
                            placeholder={t('general.links.titlePlaceholder')}
                        />
                    )}

                    {(!isSupport || mode === SUPPORT_MODES.URL) && (
                        <Input.Text
                            className="mt-2"
                            start="https://"
                            variant="dark"
                            size={180}
                            value={link}
                            onInput={(value) => {
                                const linkCopy = [...links];
                                linkCopy[index].url = value;
                                setLink(value);

                                // Auto-fill type from URL only if the user hasn't
                                // explicitly chosen a type already. Never into
                                // `support`: that row is fixed.
                                if (typeAutoFilled) {
                                    const detected = detectLinkType(value);
                                    if (detected && detected !== type && detected !== SUPPORT_TYPE) {
                                        applyTypeChange(detected, linkCopy);
                                    }
                                }

                                REF_config.current.links = [...linkCopy];
                            }}
                            label={t('general.links.urlLabel')}
                            placeholder={t('general.links.urlPlaceholder')}
                        />
                    )}
                    <div className="mt-1 link-buttons-footer">
                        {!isPrimarySupport && (
                            <Button
                                color="error"
                                onClick={() => {
                                    const linkCopy = [...links];
                                    linkCopy.splice(index, 1);
                                    REF_config.current.links = [...linkCopy];
                                    setLinks([...linkCopy]);
                                    setHasUpdates(true);
                                }}
                                endIcon={<IconTrash />}
                                text={t('common:actions.delete')}
                            />
                        )}
                        <Button
                            color="overlay"
                            onClick={() => {
                                setOpen(false);
                            }}
                            endIcon={<IconCheck />}
                            text={t('general.links.finishEditing')}
                        />
                    </div>
                </div>
            );
        };

        const linksElm =
            links?.length > 0 ? (
                <>
                    {links?.map((linkInfo, i) => (
                        <LinkInfo data={linkInfo} key={i} index={i} />
                    ))}
                </>
            ) : null;

        return (
            <div>
                <Text.h3
                    value={t('general.links.title')}
                    className="mt-4 mb-2"
                    left={<IconLink />}
                    grow
                    right={<Button
                        color="white"
                        variant="rounded"
                        onClick={() => {
                            const linksCopy = [...(links || [])];
                            linksCopy.push({ title: "", url: "", type: 'website', open: true });
                            handleChange('links', linksCopy)
                            setLinks([...linksCopy]);
                        }}
                        small
                        disabled={links?.length >= 10}
                        endIcon={<IconPlus />}
                        text={t('general.links.add')}
                    />}
                />
                {linksElm}
            </div >
        );
    }

    return (
        <div className="config-page config-general-community" key={loading}>
            {/* Status + actions, in the floating settings header. */}
            <ConfigHeaderActions>
                <Button
                    small
                    color={status === 'published' ? 'success' : 'overlay'}
                    onMouseEnter={handleOpenStatus}
                    onClick={handleOpenStatus}
                    startIcon={status === 'published' ? <IconWorld /> : <IconGeometry />}
                    endIcon={<IconChevronDown />}
                    text={status === 'published' ? t('common:status.published') : t('common:status.draft')}
                    size="small"
                />
                <Button
                    color="overlay"
                    size="small"
                    onMouseEnter={handleOpenActions}
                    onClick={handleOpenActions}
                    icon={<IconDots />}
                />
            </ConfigHeaderActions>
            <Popper controlRef={REF_popper} config={{ activateOnHover: true }} />
            <Popper controlRef={REF_statusPopper} config={{ activateOnHover: true }} />

            {loading ? <ConfigLoader /> : (
                <div className="mt-4">
                    <Input.Text
                        variant="dark"
                        className="mt-2"
                        label={t('general.fields.name')}
                        size={25}
                        value={REF_config.current.name}
                        onInput={(v) => {
                            handleChange('name', v)
                        }}
                    />
                    <Input.Text
                        variant="dark"
                        className="mt-2"
                        label={t('general.fields.description')}
                        size={250}
                        multiline
                        value={REF_config.current.description ?? ''}
                        onInput={(v) => {
                            handleChange('description', v)
                        }}
                        emoji
                    />

                    <Hero />

                    <Profile />

                    {/* The upload saves itself (own endpoint); the card's title/text
                        save with the tab's Save button through controlRef. */}
                    <WelcomeVideo
                        communityId={communityId}
                        value={REF_config.current.welcome_video}
                        onChange={(next) => { REF_config.current.welcome_video = next; }}
                        onDirty={() => setHasUpdates(true)}
                        controlRef={REF_welcomeVideo}
                    />

                    <Links />
                </div>
            )
            }

            {hasUpdates && <BottomSubmit onPress={handleSubmit} />}
        </div >
    )
}
